<template>
  <div class="mail-form">
    <a
      id="dcb-mailform"
      style="position: absolute; top: -72px"
    />
    <Heading
      size="md"
      spaces-bottom
      tag="h2"
      align="center"
      class="mail-form__heading"
    >
      Jetzt registrieren für bis zu 5&nbsp;€
    </Heading>
    <div class="mail-form__content">
      <Paragraph no-margin>
        Die Aktion<DcbFootnote /> gilt für Laufzeitverträge, die „Zahl einfach per Handyrechnung“ in
        den letzten 3 Monaten vor Kampagnenstart nicht genutzt haben. Registrierung und Kauf muss
        bis 30.06.2026 erfolgen.
      </Paragraph>
    </div>

    <div class="mail-form__image">
      <ImageElement
        alt=""
        aria-hidden="true"
        render-without-figure
        src="/content/content-images/dcb/august/1f24a8a556d4eafe7318720f0b877bfb/gewinne-08-2025-l.png"
        style="aspect-ratio: 216 / 185"
      />
    </div>

    <form class="mail-form__form">
      <TextInput
        id="mail-input"
        v-model="mail"
        :error-message="visibleErrors.mail"
        type="email"
        size="medium"
        label="E-Mail-Adresse *"
        class="mail-form__input mail-form__input-mail"
      />

      <TextInput
        id="mail-confirmation-input"
        v-model="mail2"
        :error-message="visibleErrors.mail2"
        type="email"
        size="medium"
        label="E-Mail-Adresse bestätigen *"
        class="mail-form__input mail-form__input-mail2"
      />

      <div class="mail-form__input mail-form__input-phone">
        <TextInput
          id="tel-phoneArea"
          v-model="phoneArea"
          class="mail-form__input-phone-area"
          size="medium"
          :error-message="visibleErrors.phoneArea"
          placeholder="0171"
          label="Vorwahl *"
          has-fixed-value
          type="tel"
          :maxlength="6"
        />
        <TextInput
          id="tel-phoneNumber"
          v-model="phoneNumber"
          class="mail-form__input-phone-number"
          placeholder="2225555"
          size="medium"
          :error-message="visibleErrors.phoneNumber"
          label="Mobilfunknummer *"
          has-fixed-value
          type="tel"
          :maxlength="20"
        />
      </div>

      <div class="mail-form__legal">
        <CheckboxInput
          id="agb"
          v-model="agb"
          :error-message="visibleErrors.agb"
          class="mail-form__checkbox"
          required
        >
          Ja, ich akzeptiere die
          <a
            href="/downloads/2603_Teilnahmebedingungen-und-Datenschutzhinweise_Zahl-einfach-per-Handyrechnung_Dauerkampagne.pdf"
            target="_blank"
            class="link"
            style="display: inline"
          >
            Teilnahmebedingungen und Datenschutzhinweise.
          </a>
        </CheckboxInput>

        <CheckboxInput
          id="reminder"
          v-model="reminder"
          :error-message="visibleErrors.reminder"
          class="mail-form__checkbox"
        >
          Ja, ich möchte zukünftig über Neuigkeiten und Marketingaktionen zu „Zahl einfach per
          Handyrechnung“ durch die i-Sell GmbH, Erlanger Str. 9-13, 91083 Baiersdorf persönlich
          informiert und beraten werden und stimme der Kontaktaufnahme per E-Mail, SMS und über
          andere elektronische Messengerdienste zu. Es gelten die
          <a
            href="/downloads/Optin-DCB.pdf"
            target="_blank"
            class="link"
            style="display: inline"
            >Datenschutzhinweise der i-Sell GmbH</a
          >.
        </CheckboxInput>
      </div>

      <div class="mail-form__errors">
        <template
          v-for="(msg, key) in responseErrors"
          :key="key"
        >
          <div
            class="error-message"
            v-html="msg"
          />
        </template>
      </div>

      <div class="mail-form__button">
        <LinkButton
          tag="button"
          primary
          tealium-name="dcb.mailform.register.click"
          @click.prevent="submit"
        >
          Jetzt teilnehmen
        </LinkButton>
        <br /><br />
        *Pflichtfelder
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import {
  LinkButton,
  Heading,
  Paragraph,
  ImageElement,
  CheckboxInput,
  TextInput,
} from '@i22team/td-longtail-vue-components';

import DcbFootnote from '~/components/dcb/dcb-footnote.vue';
import { useMailForm } from '~/lib/dcb/dcb-mailform';

const emits = defineEmits(['mailSent', 'showError']);

const hideForm = () => {
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, 1);

  emits('showError', false);
};

const {
  mail,
  mail2,
  phoneArea,
  phoneNumber,
  agb,
  reminder,
  submit,
  visibleErrors,
  responseErrors,
} = useMailForm({
  emits,
  dcbOptions: {
    identifier: '5euro',
    path: '/handyrechnung-aktion',
  },
  hideFormCallback: hideForm,
  specialFields: {
    reminder: {
      required: false,
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.mail-form {
  position: relative;
  margin-bottom: $grid-base * 3;
  margin-top: $grid-base;
  grid-gap: $grid-base;
  display: grid;
  grid-template-areas: 'title' 'heading' 'content' 'image' 'form' 'faq';
  grid-template-columns: 1fr;

  @include mq($mq-sm) {
    margin-top: $grid-base * 2;
    margin-bottom: $grid-base * 5;
    grid-template-columns: 600px;
    //grid-template-areas: 'title title' 'heading heading' 'content content' 'form image' 'faq faq';
    //grid-template-columns: 1fr 1fr;
    justify-content: center;
  }
  &__title {
    grid-area: title;
  }

  &__heading {
    grid-area: heading;
    @include mq(false, $mq-sm - 1) {
      margin-bottom: 0;
      font-size: 24px;
      line-height: 29px;
    }
  }

  &__content {
    grid-area: content;
    text-align: center;
    margin-bottom: $grid-base * 2;
  }

  &__form {
    grid-area: form;
    display: grid;
    grid-template-areas: 'mail1' 'mail2' 'number' 'code' 'legal' 'errors' 'button';
    grid-template-columns: 1fr;
    grid-gap: $grid-base;
    @include mq($mq-sm) {
      grid-gap: $grid-base * 2 $grid-base;
    }
  }

  &__image {
    width: 100%;
    grid-area: image;
    justify-self: flex-end;
    position: relative;
    display: none;

    @include mq(false, $mq-sm) {
      display: none;
    }

    img {
      display: block;
      width: 100%;
      aspect-ratio: 216 / 185;
      @include mq(false, $mq-sm - 1) {
        display: block;
      }
    }

    @include mq($mq-sm) {
      grid-area: image;
      margin-right: 0;

      img {
        max-height: 560px;
      }
    }
  }

  &__input {
    &-mail {
      grid-area: mail1;
    }
    &-mail2 {
      grid-area: mail2;
    }
    &-code {
      grid-area: code;
    }
    &-phone {
      grid-area: number;
      display: flex;
      align-items: baseline; // might have an error
      grid-gap: 8px;

      &-number {
        width: 100%;
      }

      &-number,
      &-area {
        :deep(.input-wrapper__input) {
          &::placeholder {
            color: var(--color-gray-500);
          }
        }
      }

      &--label {
        :deep(.input-wrapper__input) {
          width: 60px;
        }
      }
    }
  }

  &__legal {
    grid-area: legal;
  }

  &__errors {
    grid-area: errors;
  }

  &__button {
    grid-area: button;
  }

  &__checkbox {
    :deep(input) {
      right: 1px;
    }
  }

  &__faq {
    grid-area: faq;
    padding-top: 2 * $grid-base;
  }

  :deep(.checkbox-input) {
    @include mq($mq-sm) {
      margin-bottom: $grid-base;
    }
  }
}
</style>
